import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, CheckCircle2, Check, HelpCircle, Mail, Phone, UserCheck, Sparkles, LifeBuoy, Download, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { capi } from "@/lib/customer";
import BookingModal from "@/components/BookingModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

const ANSWER_BADGE = {
  yes: { label: "Yes", color: "#34D399", bg: "rgba(52,211,153,0.14)", Icon: Check },
  maybe: { label: "Maybe", color: "#FBBF24", bg: "rgba(251,191,36,0.14)", Icon: HelpCircle },
  no: { label: "No", color: "#F87171", bg: "rgba(248,113,113,0.14)", Icon: X },
  null: { label: "Not assessed", color: "#94A3B8", bg: "rgba(148,163,184,0.12)", Icon: HelpCircle },
};

function ConsultantCard({ c, onBook }) {
  if (!c) return null;
  const img = photoSrc(c.photo_url);
  return (
    <div data-testid="consultant-card" className="rounded-2xl border p-4" style={{ borderColor: "rgba(226,194,117,0.3)", background: "linear-gradient(135deg, rgba(226,194,117,0.12), rgba(13,17,26,0.4))" }}>
      <div className="flex items-center gap-2 text-amber-300"><UserCheck size={15} /><span className="text-[11px] font-semibold uppercase tracking-wider">Your point of contact</span></div>
      <div className="mt-2 flex items-center gap-3">
        {img ? (
          <img data-testid="consultant-photo" src={img} alt={c.name} className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-amber-300/40" />
        ) : (
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-amber-300/20 text-lg font-bold text-amber-200">{(c.name || "?").charAt(0)}</span>
        )}
        <div>
          <p className="text-base font-semibold text-white">{c.name}</p>
          <p className="text-xs text-slate-300">{c.title}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        <a data-testid="consultant-email" href={`mailto:${c.email}`} className="flex items-center gap-2 text-sm text-amber-200"><Mail size={14} /> {c.email}</a>
        <a data-testid="consultant-mobile" href={`tel:${(c.mobile || "").replace(/\s/g, "")}`} className="flex items-center gap-2 text-sm text-amber-200"><Phone size={14} /> {c.mobile}</a>
      </div>
      {onBook && (
        <button data-testid="consultant-book-btn" onClick={onBook}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-amber-300/40 bg-amber-300/10 py-2.5 text-xs font-semibold text-amber-200">
          <CalendarClock size={14} /> Book a 30-minute private session
        </button>
      )}
    </div>
  );
}

export default function CoordinationSheet({ journey, onClose }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    if (!journey) return;
    setSummary(null); setLoading(true); setSent(false);
    capi.get(`/customer/journeys/${journey.id}/summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [journey]);

  const send = async () => {
    setSending(true);
    try {
      await capi.post(`/customer/journeys/${journey.id}/coordinate`);
      setSent(true);
    } catch { /* ignore */ } finally { setSending(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await capi.get(`/customer/journeys/${journey.id}/assessment.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `readiness-${(journey.country_name || "assessment").replace(/\s/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); } finally { setPdfBusy(false); }
  };

  const emailMe = async () => {
    setEmailBusy(true);
    try {
      const { data } = await capi.post(`/customer/journeys/${journey.id}/assessment/email`);
      toast.success(`Sent to ${data.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not email your assessment");
    } finally { setEmailBusy(false); }
  };

  const rd = summary?.readiness;

  return (
    <>
    <AnimatePresence>
      {journey && (
        <>
          <motion.div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div
            data-testid="coordination-modal"
            className="fixed inset-x-0 bottom-0 z-[71] mx-auto flex max-h-[90vh] max-w-lg flex-col rounded-t-3xl border-t border-white/10"
            style={{ background: "rgba(10,14,23,0.99)", boxShadow: "0 -20px 60px rgba(0,0,0,0.6)" }}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="flex justify-center pt-3"><div className="h-1.5 w-12 rounded-full bg-white/20" /></div>
            <div className="flex items-start justify-between px-5 pt-3">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">Request Coordination</p>
                <h2 className="mt-1 flex items-center gap-2 text-xl font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>
                  <span className="text-2xl">{journey.flag}</span> {journey.country_name}
                </h2>
              </div>
              <button data-testid="coordination-close" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/10 text-slate-300"><X size={16} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-7 pt-4">
              {loading ? (
                <div className="flex items-center gap-2 py-10 text-sm text-slate-400"><Loader2 size={16} className="animate-spin" /> Preparing your assessment…</div>
              ) : sent ? (
                <div data-testid="coordination-success" className="flex flex-col items-center py-6 text-center">
                  <div className="grid h-16 w-16 place-items-center rounded-2xl bg-emerald-400/15 text-emerald-300"><CheckCircle2 size={30} /></div>
                  <h3 className="mt-4 text-lg font-semibold text-white">Assessment shared</h3>
                  <p className="mt-1 max-w-xs text-sm text-slate-300">
                    <strong className="text-amber-200">{summary?.consultant?.name}</strong>, {summary?.consultant?.title}, will review your situation and be in touch.
                  </p>
                  <div className="mt-5 w-full"><ConsultantCard c={summary?.consultant} onBook={() => setBooking(true)} /></div>
                  <div className="mt-4 grid w-full grid-cols-2 gap-2">
                    <button data-testid="coordination-download-pdf" onClick={downloadPdf} disabled={pdfBusy} className="flex items-center justify-center gap-1.5 rounded-xl border border-white/12 py-2.5 text-xs font-semibold text-slate-200 disabled:opacity-60">
                      {pdfBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download PDF
                    </button>
                    <button data-testid="coordination-email-me" onClick={emailMe} disabled={emailBusy} className="flex items-center justify-center gap-1.5 rounded-xl border border-white/12 py-2.5 text-xs font-semibold text-slate-200 disabled:opacity-60">
                      {emailBusy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email it to me
                    </button>
                  </div>
                  <button data-testid="coordination-done" onClick={onClose} className="mt-4 rounded-full bg-amber-300 px-6 py-2.5 text-sm font-semibold text-[#07090E]">Done</button>
                </div>
              ) : (
                <>
                  <ConsultantCard c={summary?.consultant} onBook={() => setBooking(true)} />
                  <p className="mt-3 text-xs text-slate-300">
                    We'll share your full self-assessment below so <strong>{summary?.consultant?.name}</strong> can review your situation before advising. No need to re-enter your details — we'll use your account.
                  </p>

                  {rd && (
                    <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
                      <span className="rounded-full px-2.5 py-1 font-semibold text-emerald-300" style={{ background: "rgba(52,211,153,0.12)" }}>{rd.ready_steps}/{rd.total_steps} steps ready</span>
                      <span className="flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold text-emerald-300" style={{ background: "rgba(52,211,153,0.12)" }}><Sparkles size={11} /> {rd.strengths} strength{rd.strengths === 1 ? "" : "s"}</span>
                      <span className="flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold text-amber-300" style={{ background: "rgba(251,146,60,0.14)" }}><LifeBuoy size={11} /> {rd.flags.length} to discuss</span>
                    </div>
                  )}

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button data-testid="coordination-download-pdf" onClick={downloadPdf} disabled={pdfBusy} className="flex items-center justify-center gap-1.5 rounded-xl border border-white/12 py-2.5 text-xs font-semibold text-slate-200 disabled:opacity-60">
                      {pdfBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download PDF
                    </button>
                    <button data-testid="coordination-email-me" onClick={emailMe} disabled={emailBusy} className="flex items-center justify-center gap-1.5 rounded-xl border border-white/12 py-2.5 text-xs font-semibold text-slate-200 disabled:opacity-60">
                      {emailBusy ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email it to me
                    </button>
                  </div>

                  <p className="mt-5 text-[11px] font-medium uppercase tracking-wider text-slate-400">Your self-assessment</p>
                  <div className="mt-2 space-y-3" data-testid="coordination-summary">
                    {(summary?.steps || []).map((s) => (
                      <div key={s.id} className="rounded-xl border border-white/[0.06] bg-white/[0.03] p-3">
                        <p className="text-[13px] font-semibold text-slate-100">{s.id} · {s.title}</p>
                        <ul className="mt-2 space-y-1.5">
                          {s.requirements.map((r, i) => {
                            const b = ANSWER_BADGE[r.answer ?? "null"] || ANSWER_BADGE.null;
                            const Icon = b.Icon;
                            return (
                              <li key={i} className="flex items-center justify-between gap-2">
                                <span className="text-[12px] text-slate-300">{r.label}</span>
                                <span className="flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ color: b.color, background: b.bg }}>
                                  <Icon size={10} /> {b.label}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {!loading && !sent && (
              <div className="border-t border-white/10 px-5 py-3">
                <button data-testid="coordination-send" onClick={send} disabled={sending}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-300 py-3.5 text-sm font-semibold text-[#07090E] transition-transform active:scale-[0.98] disabled:opacity-60">
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {sending ? "Sharing…" : `Share My Assessment With ${(summary?.consultant?.name || "My Consultant").split(" ")[0]}`}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
    <BookingModal open={booking} onClose={() => setBooking(false)} journey={journey} consultant={summary?.consultant} />
    </>
  );
}
